import { Table } from "antd";
import React, { useMemo, useState } from "react";
import AdoptionNestedTable from "./AdoptionNestedTable";
import "./Adoption.css";
import SuperAdminContainer from "../SuperAdminContainer";
import { ChantInput, ScreenHeader } from "../../../CommonComponents";
import useGetStartedDetails from "../../GettingStarted/MaximizedView/hooks/useGetStartedDetails";
import useGroupsData from "../group/useGroupsData";

const Adoption = () => {
  const { gettingStartedTopicsCount } = useGetStartedDetails();
  const { groupsList, isLoading } = useGroupsData();
  const [search, setSearch] = useState("");

  const columns = [
    {
      align: "center",
      title: "Group",
      dataIndex: "groupName",
      key: "groupName",
      className: "header-white",
      width: 300,
    },
    {
      align: "center",
      title: "% Reg",
      width: window.screen.width > 500 ? 100 : 50,
      className: "header-white",
      key: "percentageRegistered",
      render: (data) => {
        const rm = data.registeredMembers || 0;
        const tm = data.totalMembers || 0;
        const val = (rm / tm) * 100;
        return (Number.isNaN(val) ? 0 : Math.round(val)) + "%";
      },
    },
    {
      align: "center",
      title: "Reg",
      width: window.screen.width > 500 ? 105 : 80,
      key: "registeredMembers",
      className: "header-white",
      dataIndex: "registeredMembers",
      render: (registeredMembers) => registeredMembers || 0,
      sorter: (a, b) => {
        const regA = a.registeredMembers || 0;
        const regB = b.registeredMembers || 0;
        return regA - regB;
      },
    },
    {
      align: "center",
      title: "Adoption %",
      width: 150,
      key: "adoptionPercentage",
      className: "header-white",
      dataIndex: "gettingStarted",
      render: (gettingStarted) => {
        const totalCompletedItems = Object.values(gettingStarted || {}).reduce(
          (acc, curr) => acc + (curr?.completedItems || 0),
          0,
        );
        return `${Math.floor((totalCompletedItems / gettingStartedTopicsCount.totalCount) * 100) || 0}%`;
      },
      sorter: (a, b) => {
        const totalCompletedItemsA = Object.values(
          a.gettingStarted || {},
        ).reduce((acc, curr) => acc + (curr?.completedItems || 0), 0);
        const totalCompletedItemsB = Object.values(
          b.gettingStarted || {},
        ).reduce((acc, curr) => acc + (curr?.completedItems || 0), 0);
        const adoptionPercentageA =
          totalCompletedItemsA / gettingStartedTopicsCount.totalCount;
        const adoptionPercentageB =
          totalCompletedItemsB / gettingStartedTopicsCount.totalCount;
        return adoptionPercentageA - adoptionPercentageB;
      },
    },
    {
      align: "center",
      title: "Setup",
      dataIndex: ["gettingStarted", "setup", "completedItems"],
      key: "setup",
      render: (data) =>
        `${Math.floor((data / gettingStartedTopicsCount.setup) * 100) || 0}%`,
      className: "header-white",
      width: 300,
    },
    {
      align: "center",
      title: "Onboard",
      dataIndex: ["gettingStarted", "onboard", "completedItems"],
      render: (data) =>
        `${Math.floor((data / gettingStartedTopicsCount.onboard) * 100) || 0}%`,
      key: "onboard",
      className: "header-white",
      width: 300,
    },
    {
      align: "center",
      title: "Engage",
      dataIndex: ["gettingStarted", "engage", "completedItems"],
      render: (data) =>
        `${Math.floor((data / gettingStartedTopicsCount.engage) * 100) || 0}%`,
      key: "engage",
      className: "header-white",
      width: 300,
    },
    {
      align: "center",
      title: "Grow",
      dataIndex: ["gettingStarted", "grow", "completedItems"],
      render: (data) =>
        `${Math.floor((data / gettingStartedTopicsCount.grow) * 100) || 0}%`,
      key: "grow",
      className: "header-white",
      width: 300,
    },
    {
      align: "center",
      title: "Owner",
      dataIndex: "owner",
      render: (data) => data || "-",
      key: "owner",
      className: "header-white",
      width: window.screen.width > 500 ? 100 : 50,
    },
    {
      align: "center",
      title: "Club Name",
      dataIndex: "clubName",
      render: (data) => data || "-",
      key: "clubName",
      className: "header-white",
      width: window.screen.width > 500 ? 200 : 150,
    },
  ];

  // Filter groups based on search
  const filteredGroups = useMemo(() => {
    if (!search) return groupsList || [];

    return (groupsList || []).filter((group) =>
      group.groupName?.toLowerCase().includes(search.toLowerCase()),
    );
  }, [groupsList, search]);

  return (
    <SuperAdminContainer>
      <ScreenHeader title="Adoption" elementCount={filteredGroups.length} />
      <div
        style={{
          marginTop: "2px",
          backgroundColor: "var(--primary-white-background)",
          padding: "8px 12px",
          display: "flex",
          gap: "12px",
        }}
      >
        <ChantInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          name="groupName"
          placeholder="Search by group"
          tabIndex="2"
          style={{ width: 220 }}
        />
      </div>
      <Table
        columns={columns}
        bordered
        dataSource={filteredGroups}
        loading={isLoading}
        expandable={{
          expandedRowRender: (record) => (
            <AdoptionNestedTable data={record} loading={isLoading} />
          ),
        }}
        rowKey="id"
        bodyStyle={{
          backgroundColor: "#ffffff",
        }}
        scroll={{ x: true }}
        pagination={{
          position: ["bottomLeft"],
          defaultPageSize: 50,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
        }}
      />
    </SuperAdminContainer>
  );
};

export default Adoption;
